import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * RefreshControl — small inline chip for every admin tab header (iter163).
 * Shows "Updated Xs ago" and provides a manual refresh button that triggers
 * the same `refresh` fn returned by `useAutoRefresh`.
 *
 * Props:
 *   lastUpdated — Date | null (from useAutoRefresh)
 *   onRefresh   — () => Promise<void> (bound to the hook's refresh)
 *   loading     — boolean (optional — show spinning icon while fetching)
 *   label       — optional label prefix (default "Updated")
 */
const RefreshControl = ({ lastUpdated, onRefresh, loading = false, label = "Updated" }) => {
  const [, tick] = useState(0);

  // Re-render every 10s so the "Xs ago" counter stays accurate.
  useEffect(() => {
    if (!lastUpdated) return undefined;
    const id = setInterval(() => tick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  const ago = (() => {
    if (!lastUpdated) return "just now";
    const sec = Math.max(0, Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    if (sec < 5) return "just now";
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    return `${Math.floor(sec / 3600)}h ago`;
  })();

  return (
    <button
      type="button"
      onClick={() => onRefresh && onRefresh()}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 disabled:cursor-wait transition-colors"
      title="Refresh now"
      data-testid="refresh-control"
    >
      <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
      <span>{label} {ago}</span>
    </button>
  );
};

export default RefreshControl;
